import { useMemo } from 'react';
import { useCurrentUserOperableHubs } from './useCurrentUserOperableWarehouses';
import { useCurrentUserOperableWarehouseIds } from './useCurrentUserOperableWarehouseIds';
import { CurrentUserOperableWarehousesResponse } from '../graphql/queries/currentUserOperableWarehouses';

export type OperableHubOption = { value: number; label: string };

export type OperableHubListItem = { id: number; name: string };

/** 数据看板默认不展示的测试 Hub（仅 UI 过滤，权限仍以接口为准） */
export const DASHBOARD_EXCLUDE_HUB_LABELS = ['TEST1 HUB', 'TEST2 HUB'] as const;

type OperableHubWarehouse = CurrentUserOperableWarehousesResponse['warehouses'][number];

type UseOperableHubSelectOptionsConfig = {
    excludeLabels?: string[];
    allOptionLabel?: string;
    /** true / false / auto：多 Hub 或全量仓权限时展示「全部」(value=0) */
    showAllOption?: boolean | 'auto';
    /** 仅保留指定 businessType 的 hub，不传则不过滤 */
    filterBusinessTypes?: number[];
};

export function getHubDisplayLabel(hub: Pick<OperableHubWarehouse, 'id' | 'name' | 'sortingCode'>): string {
    return hub.name || hub.sortingCode || `Hub-${hub.id}`;
}

/**
 * Hub 下拉：列表来自 useCurrentUserOperableHubs，是否全美来自可操作仓 ID 是否含 0。
 */
export function useOperableHubSelectOptions(config?: UseOperableHubSelectOptionsConfig) {
    const {
        excludeLabels = [],
        allOptionLabel = '全部',
        showAllOption = 'auto',
        filterBusinessTypes,
    } = config || {};

    const { warehouses: operableHubs, loading: hubsLoading, error: hubsError, refetch } =
        useCurrentUserOperableHubs();
    const { hasFullWarehouseAccess, loading: idsLoading } = useCurrentUserOperableWarehouseIds();

    const filteredHubs = useMemo(() => {
        let hubs = operableHubs;
        hubs = hubs.filter((hub) => !getHubDisplayLabel(hub).toUpperCase().startsWith('TEST'));
        if (excludeLabels.length > 0) {
            hubs = hubs.filter((hub) => !excludeLabels.includes(getHubDisplayLabel(hub)));
        }
        if (filterBusinessTypes && filterBusinessTypes.length > 0) {
            hubs = hubs.filter((hub) => hub.businessType != null && filterBusinessTypes.includes(hub.businessType));
        }
        return hubs;
    }, [operableHubs, excludeLabels, filterBusinessTypes]);

    const shouldShowAllOption = useMemo(() => {
        if (showAllOption === true) return true;
        if (showAllOption === false) return false;
        return filteredHubs.length > 1 || hasFullWarehouseAccess;
    }, [showAllOption, filteredHubs.length, hasFullWarehouseAccess]);

    const { hubOptions, hubListForDrawer, singleAccessibleHubId } = useMemo(() => {
        const opts: OperableHubOption[] = [];
        const drawer: OperableHubListItem[] = [];
        if (shouldShowAllOption) {
            opts.push({ value: 0, label: allOptionLabel });
        }
        filteredHubs.forEach((hub) => {
            const id = Number(hub.id);
            const label = getHubDisplayLabel(hub);
            opts.push({ value: id, label });
            drawer.push({ id, name: label });
        });
        return {
            hubOptions: opts,
            hubListForDrawer: drawer,
            singleAccessibleHubId: filteredHubs.length === 1 ? Number(filteredHubs[0].id) : null,
        };
    }, [filteredHubs, shouldShowAllOption, allOptionLabel]);

    return {
        hubOptions,
        hubListForDrawer,
        operableHubs: filteredHubs,
        hasFullWarehouseAccess,
        singleAccessibleHubId,
        loading: hubsLoading || idsLoading,
        error: hubsError,
        refetch,
    };
}
